import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id = '' } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(128);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-10 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const contentParagraphs = article.content.split('\n\n').filter(Boolean);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.excerpt,
    'author': {
      '@type': 'Person',
      'name': article.author,
    },
    'datePublished': article.publishDate,
    'image': article.thumbnail,
    'publisher': {
      '@type': 'Organization',
      'name': '蘑菇视频',
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://mogushipin.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': '首页',
        'item': 'https://mogushipin.com',
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': article.category,
        'item': `https://mogushipin.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 蘑菇视频`}
        description={article.excerpt}
        keywords={`蘑菇视频,${article.tags.join(',')},影视资讯`}
        canonicalUrl={`https://mogushipin.com/article/${article.id}`}
        ogType="article"
        ogImage={article.thumbnail}
        schema={schema}
      />

      {/* Breadcrumb schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="pt-20 pb-10 min-h-screen bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: article.category, path: `/category/${article.categorySlug}` },
              { name: article.title },
            ]}
          />

          {/* Header */}
          <header className="mb-8">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-user-line text-primary-500 text-xs"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views} 阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-8 bg-background-200">
            <img
              src={article.thumbnail}
              alt={article.title}
              title={`${article.title} - 蘑菇视频影视资讯`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="prose-custom max-w-none">
            {contentParagraphs.map((paragraph, idx) => {
              const trimmed = paragraph.trim();
              if (trimmed.startsWith('## ')) {
                const headingText = trimmed.replace('## ', '');
                const headingId = `section-${idx}`;
                return (
                  <h2 key={idx} id={headingId} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-8 mb-4">
                    {headingText}
                  </h2>
                );
              }
              if (trimmed.startsWith('### ')) {
                return (
                  <h3 key={idx} className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mt-6 mb-3">
                    {trimmed.replace('### ', '')}
                  </h3>
                );
              }
              if (trimmed.startsWith('- ')) {
                const items = trimmed.split('\n').filter((l) => l.trim().startsWith('- '));
                return (
                  <ul key={idx} className="list-disc pl-6 mb-4 space-y-1">
                    {items.map((item, i) => (
                      <li key={i} className="text-foreground-700 leading-relaxed">{item.replace('- ', '')}</li>
                    ))}
                  </ul>
                );
              }
              if (trimmed.match(/^\d\.\s/)) {
                const items = trimmed.split('\n').filter((l) => l.match(/^\d\.\s/));
                return (
                  <ol key={idx} className="list-decimal pl-6 mb-4 space-y-1">
                    {items.map((item, i) => (
                      <li key={i} className="text-foreground-700 leading-relaxed">{item.replace(/^\d\.\s/, '')}</li>
                    ))}
                  </ol>
                );
              }
              return (
                <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-base">
                  {trimmed}
                </p>
              );
            })}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Like button */}
          <div className="flex items-center justify-center mt-8 pb-8 border-b border-background-200">
            <button
              onClick={() => {
                setLiked(!liked);
                setLikeCount((c) => liked ? c - 1 : c + 1);
              }}
              className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                liked
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
              {likeCount} 赞
            </button>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-8">
              <h4 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                相关文章
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group block bg-background-50 rounded-xl border border-background-200 p-3 transition-all hover:border-primary-200 cursor-pointer"
                  >
                    <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-3">
                      <img
                        src={ra.thumbnail}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <h5 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h5>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}